import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DollarSign, CreditCard, AlertTriangle, CheckCircle,
  Pause, Play, Ban, RefreshCw, TrendingUp, Users, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import MasterBillingControls from './MasterBillingControls';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const statusColors = {
  active: 'bg-green-500/10 text-[#00CC99] border border-green-500/20',
  past_due: 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20',
  suspended: 'bg-red-500/10 text-[#FF6B6B] border border-red-500/20',
  cancelled: 'bg-white/5 text-[#64748B] border border-white/10',
  paused: 'bg-blue-500/10 text-[#0075FF] border border-blue-500/20',
  no_subscription: 'bg-white/5 text-[#64748B] border border-white/10',
};

const statusLabels = {
  active: 'Active', past_due: 'Past Due', suspended: 'Suspended',
  cancelled: 'Cancelled', paused: 'Paused', no_subscription: 'No Subscription',
};

export default function MasterBilling() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const token = localStorage.getItem('masterToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API}/master/billing`, { headers });
      setBilling(res.data);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBilling(); }, []);

  const handleAction = async (siteId, action) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/master/billing/${siteId}`, { action }, { headers });
      toast.success(`Subscription ${action}d successfully`);
      fetchBilling();
      setSelectedSub(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOverdue = async () => {
    try {
      const res = await axios.post(`${API}/master/billing/check-overdue`, {}, { headers });
      toast.success(`Checked ${res.data.checked} subscriptions. ${res.data.actions.length} actions taken.`);
      fetchBilling();
    } catch (error) {
      toast.error('Failed to check overdue subscriptions');
    }
  };

  const sendPaymentLink = (siteId) => {
    const payUrl = `${window.location.origin}/billing/pay?site=${siteId}`;
    navigator.clipboard.writeText(payUrl);
    toast.success('Payment link copied to clipboard!');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;
  }

  const statCards = [
    { label: 'Monthly Revenue', value: `$${billing?.total_mrr?.toFixed(2) || '0.00'}`, icon: TrendingUp, gradient: 'from-[#00CC99] to-[#10B981]' },
    { label: 'Active', value: billing?.active_subscriptions || 0, icon: CheckCircle, gradient: 'from-[#3B82F6] to-[#06B6D4]' },
    { label: 'Overdue', value: billing?.overdue_count || 0, icon: AlertTriangle, gradient: 'from-[#F59E0B] to-[#EF4444]' },
    { label: 'Total Sites', value: billing?.total_sites || 0, icon: Users, gradient: 'from-[#8B5CF6] to-[#EC4899]' },
  ];

  return (
    <div className="space-y-6" data-testid="master-billing">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white" data-testid="billing-title">Billing & Subscriptions</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Manage client subscriptions ($30/month)</p>
        </div>
        <button onClick={handleCheckOverdue} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white/5 border border-white/10 text-[#94A3B8] hover:text-white hover:bg-white/10 transition-all" data-testid="check-overdue-btn">
          <RefreshCw size={14} /> Check Overdue
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="billing-stats">
        {statCards.map((stat) => (
          <GlassCard key={stat.label} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#64748B] uppercase tracking-wider font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${stat.gradient} shadow-lg`}>
                <stat.icon size={16} className="text-white" />
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Subscriptions Table */}
      <GlassCard className="overflow-hidden" data-testid="subscriptions-table">
        <div className="px-6 py-5 border-b border-white/5">
          <h2 className="font-semibold text-lg text-white">All Subscriptions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Site</th>
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Status</th>
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Amount</th>
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Last Payment</th>
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Next Billing</th>
                <th className="px-6 py-3 text-xs uppercase tracking-wider text-[#64748B] font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {billing?.subscriptions?.map((sub) => (
                <tr key={sub.site_id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`sub-row-${sub.site_id}`}>
                  <td className="px-6 py-4">
                    <p className="font-medium text-white">{sub.site_name}</p>
                    <p className="text-xs text-[#64748B]">{sub.domain}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-semibold ${statusColors[sub.subscription_status] || statusColors.no_subscription}`}>
                      {sub.subscription_status === 'past_due' && <AlertTriangle size={12} />}
                      {statusLabels[sub.subscription_status] || sub.subscription_status}
                    </span>
                    {sub.reminders_sent > 0 && sub.subscription_status === 'past_due' && (
                      <p className="text-xs text-yellow-400 mt-1">{sub.reminders_sent} reminder(s) sent</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-[#94A3B8]">{sub.amount > 0 ? `$${sub.amount.toFixed(2)}/mo` : '-'}</td>
                  <td className="px-6 py-4 text-[#94A3B8] text-sm">{sub.last_payment_date ? new Date(sub.last_payment_date).toLocaleDateString() : '-'}</td>
                  <td className="px-6 py-4 text-[#94A3B8] text-sm">
                    {sub.next_billing_date ? new Date(sub.next_billing_date).toLocaleDateString() : '-'}
                    {sub.grace_period_end && <p className="text-xs text-[#FF6B6B]">Grace ends: {new Date(sub.grace_period_end).toLocaleDateString()}</p>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedSub(sub)} className="px-3 py-1.5 text-xs rounded-lg bg-white/5 border border-white/10 text-[#94A3B8] hover:text-white transition-colors" data-testid={`manage-sub-${sub.site_id}`}>Manage</button>
                      <button onClick={() => sendPaymentLink(sub.site_id)} className="px-3 py-1.5 text-xs rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 text-[#A78BFA] hover:bg-[#8B5CF6]/20 transition-colors" data-testid={`send-link-${sub.site_id}`}>Copy Pay Link</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Recent Payments */}
      {billing?.recent_payments?.length > 0 && (
        <GlassCard className="overflow-hidden" data-testid="recent-payments">
          <div className="px-6 py-5 border-b border-white/5">
            <h2 className="font-semibold text-lg text-white">Recent Payments</h2>
          </div>
          <div className="divide-y divide-white/5">
            {billing.recent_payments.map((payment) => {
              const site = billing.subscriptions?.find(s => s.site_id === payment.site_id);
              return (
                <div key={payment.id} className="px-6 py-3.5 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">{site?.site_name || payment.site_id}</p>
                    <p className="text-xs text-[#64748B]">{new Date(payment.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-medium ${payment.status === 'paid' ? 'text-[#00CC99]' : 'text-[#94A3B8]'}`}>${payment.amount?.toFixed(2)}</span>
                    <span className={`px-2.5 py-1 text-xs rounded-full font-semibold ${payment.status === 'paid' ? 'bg-green-500/10 text-[#00CC99] border border-green-500/20' : 'bg-white/5 text-[#64748B] border border-white/10'}`}>{payment.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {/* Iter 175g — Billing Controls merged in below as a second section
          so master only needs one tab to see + edit billing. The legacy
          /master/billing/controls route still renders the bare component
          for deep links. */}
      <div className="pt-8 mt-8 border-t border-white/5" data-testid="billing-controls-section">
        <MasterBillingControls />
      </div>

      {/* Manage Subscription Dialog */}
      <Dialog open={!!selectedSub} onOpenChange={() => setSelectedSub(null)}>
        <DialogContent className="bg-[#1E1E3A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Manage Subscription</DialogTitle>
          </DialogHeader>
          {selectedSub && (
            <div className="space-y-4" data-testid="manage-sub-dialog">
              <div className="bg-white/5 border border-white/5 p-4 rounded-xl space-y-2 text-sm">
                <p><span className="text-[#64748B]">Site:</span> <span className="text-white">{selectedSub.site_name}</span></p>
                <p><span className="text-[#64748B]">Status:</span> <span className={statusColors[selectedSub.subscription_status]?.split(' ').find(c => c.startsWith('text-')) || 'text-white'}> {statusLabels[selectedSub.subscription_status]}</span></p>
                <p><span className="text-[#64748B]">Domain:</span> <span className="text-[#94A3B8]">{selectedSub.domain}</span></p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {selectedSub.subscription_status === 'active' && (
                  <>
                    <button onClick={() => handleAction(selectedSub.site_id, 'pause')} disabled={actionLoading} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-500/10 text-[#0075FF] border border-blue-500/20 hover:bg-blue-500/20 text-sm disabled:opacity-50 transition-colors" data-testid="pause-sub-btn"><Pause size={14} /> Pause</button>
                    <button onClick={() => handleAction(selectedSub.site_id, 'cancel')} disabled={actionLoading} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-red-500/10 text-[#FF6B6B] border border-red-500/20 hover:bg-red-500/20 text-sm disabled:opacity-50 transition-colors" data-testid="cancel-sub-btn"><Ban size={14} /> Cancel</button>
                  </>
                )}
                {(selectedSub.subscription_status === 'paused' || selectedSub.subscription_status === 'past_due' || selectedSub.subscription_status === 'suspended') && (
                  <button onClick={() => handleAction(selectedSub.site_id, 'resume')} disabled={actionLoading} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/10 text-[#00CC99] border border-green-500/20 hover:bg-green-500/20 text-sm disabled:opacity-50 transition-colors" data-testid="resume-sub-btn"><Play size={14} /> Resume</button>
                )}
                {selectedSub.subscription_status !== 'active' && (
                  <button onClick={() => handleAction(selectedSub.site_id, 'mark_paid')} disabled={actionLoading} className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-500/10 text-[#00CC99] border border-green-500/20 hover:bg-green-500/20 text-sm disabled:opacity-50 transition-colors" data-testid="mark-paid-btn"><DollarSign size={14} /> Mark Paid</button>
                )}
                <button onClick={() => sendPaymentLink(selectedSub.site_id)} className="col-span-2 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#8B5CF6]/10 text-[#A78BFA] border border-[#8B5CF6]/20 hover:bg-[#8B5CF6]/20 text-sm transition-colors" data-testid="copy-link-btn"><CreditCard size={14} /> Copy Payment Link</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
